<template>
  <lzd-modal
    form
    :width="1200"
    :position="'center'"
    :model-value="modelValue"
    title="手写签名"
    :bodyStyle="{ background: '#f0f2f5' }"
    :close-on-click-modal="false"
    @update:modelValue="updateModelValue"
    @opened="handleOpen"
  >
    <div class="sign-container">
      <canvas
        ref="canvas"
        @mousedown="start"
        @mousemove="move"
        @mouseup="end"
        @mouseleave="end"
        @touchstart.prevent="start"
        @touchmove.prevent="move"
        @touchend.prevent="end"
      ></canvas>

      <div class="toolbar">
        <el-button @click="clearCanvas">清空</el-button>
        <el-button @click="undo">撤销</el-button>
        <el-button @click="download">保存为图片</el-button>
      </div>
    </div>

    <template #footer>
      <el-button type="success" @click="replace">替换为签名</el-button>
      <el-button @click="updateModelValue(false)">取消</el-button>
    </template>
  </lzd-modal>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import fileUtil from '@/utils/file-util';
  import { EleMessage } from 'ele-admin-plus';

  const props = defineProps<{
    /** 弹窗是否打开 */
    modelValue: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'done', file?: File): void;
    (e: 'update:modelValue', modelValue: boolean): void;
  }>();

  /** 更新modelValue */
  const updateModelValue = (value: boolean) => {
    emit('update:modelValue', value);
  };

  const replace = () => {
    if (!canvas.value) {
      EleMessage.error('未填写完成，不能替换！');
      return;
    }

    const img = cropCanvas(canvas.value!);
    const dataURL = img.toDataURL('image/png');
    const file = fileUtil.base64ToFile(dataURL);
    emit('done', file);
    updateModelValue(false);
  };

  const canvas = ref<HTMLCanvasElement>();
  const ctx = ref<CanvasRenderingContext2D>();
  const isDrawing = ref(false);
  const lastPoint = ref<{ x: number; y: number } | null>(null);
  const history: ImageData[] = [];

  // 撤销栈
  function getPos(e: MouseEvent | TouchEvent) {
    const rect = canvas.value!.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  function start(e: MouseEvent | TouchEvent) {
    isDrawing.value = true;
    lastPoint.value = getPos(e);
  }
  function move(e: MouseEvent | TouchEvent) {
    if (!isDrawing.value || !ctx.value || !lastPoint.value) {
      return false;
    }
    const pos = getPos(e);
    ctx.value.beginPath();
    ctx.value.moveTo(lastPoint.value.x, lastPoint.value.y);
    ctx.value.lineTo(pos.x, pos.y);
    ctx.value.strokeStyle = '#000';
    ctx.value.lineWidth = 2;
    ctx.value.stroke();
    lastPoint.value = pos;
  }
  function end() {
    if (!ctx.value) {
      return;
    }
    isDrawing.value = false;
    history.push(
      ctx.value.getImageData(0, 0, canvas.value!.width, canvas.value!.height)
    );
  }
  function clearCanvas() {
    ctx.value?.clearRect(0, 0, canvas.value!.width, canvas.value!.height);
    history.length = 0;
  }
  function undo() {
    if (history.length) {
      if (history.length == 1) {
        clearCanvas();
      } else {
        ctx.value?.putImageData(history.pop()!, 0, 0);
      }
    }
  }
  function download() {
    const img = cropCanvas(canvas.value!);
    const url = img.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = url;
    link.download = '用户签名.png';
    link.click();
  }

  function cropCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
    const ctx = source.getContext('2d')!;
    const { width, height } = source;

    // 获取 ImageData
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    let minX = width,
      maxX = 0,
      minY = height,
      maxY = 0;

    // 扫描像素
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4 + 3; // alpha 通道
        if (data[idx] > 0) {
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
        }
      }
    }

    // 全是空白
    if (maxX < minX || maxY < minY) return source;

    // 新建裁剪后的 canvas
    const w = maxX - minX + 1;
    const h = maxY - minY + 1;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const newCtx = canvas.getContext('2d')!;
    newCtx.putImageData(ctx.getImageData(minX, minY, w, h), 0, 0);
    return canvas;
  }

  const handleOpen = () => {
    const c = canvas.value!;
    c.width = canvas.value!.offsetWidth;
    c.height = 387;
    ctx.value = c.getContext('2d')!;
    ctx.value.lineCap = 'round';
    ctx.value.lineJoin = 'round';
  };
</script>

<style scoped>
  .sign-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: #ffffff;
  }
  canvas {
    border: 1px solid #ccc;
    margin-top: 30px;
    width: 100%;
    height: 387px;
  }
  .toolbar {
    margin: 10px;
  }
  .toolbar button {
    margin: 0 4px;
  }
</style>
